/**
 * RiskMatrix Component
 *
 * Interactive 5×5 matrix for selecting risk levels (likelihood × severity).
 * Color-coded by risk level with hover states and legend.
 *
 * @module components/RiskMatrix
 */

import React, { useState } from 'react';
import { getRiskLevel, LIKELIHOOD_SCALE, SEVERITY_SCALE } from '../config/moduleConfig';

/**
 * Get cell background color based on risk score
 * @param {number} score - Risk score (1-25)
 * @returns {string} Tailwind CSS color class
 */
const getCellColor = (score) => {
  if (score >= 20) return 'bg-red-500 hover:bg-red-600';
  if (score >= 12) return 'bg-orange-500 hover:bg-orange-600';
  if (score >= 6) return 'bg-yellow-500 hover:bg-yellow-600';
  return 'bg-green-500 hover:bg-green-600';
};

/**
 * Get ALARP zone label
 * @param {number} score - Risk score (1-25)
 * @returns {string} ALARP zone label
 */
const getAlarpLabel = (score) => {
  if (score >= 20) return 'Intolerable';
  if (score >= 6) return 'ALARP';
  return 'Acceptable';
};

/**
 * RiskMatrix Component
 * @param {Object} props - Component props
 * @param {number} props.selectedLikelihood - Currently selected likelihood (1-5)
 * @param {number} props.selectedSeverity - Currently selected severity (1-5)
 * @param {Function} props.onSelect - Callback when cell is selected (likelihood, severity)
 * @param {boolean} props.readonly - If true, matrix is not interactive
 * @param {boolean} props.compact - If true, use smaller cell sizes
 */
export const RiskMatrix = ({
  selectedLikelihood = 1,
  selectedSeverity = 1,
  onSelect,
  readonly = false,
  compact = false
}) => {
  const [hoveredCell, setHoveredCell] = useState(null);

  const isSelected = (likelihood, severity) => {
    return selectedLikelihood === likelihood && selectedSeverity === severity;
  };

  const cellSize = compact ? 'w-10 h-10' : 'w-14 h-14';
  const labelSize = compact ? 'w-10' : 'w-12';
  const fontSize = compact ? 'text-sm' : 'text-lg';

  return (
    <div className="space-y-4">
      {/* Matrix Grid */}
      <div className="inline-block">
        {/* Top labels row (Severity 1-5) */}
        <div className="flex">
          {/* Empty corner cell */}
          <div className={`${labelSize} h-8`} />
          {/* Severity labels */}
          {[1, 2, 3, 4, 5].map(severity => (
            <div
              key={severity}
              className={`${cellSize} flex items-center justify-center`}
            >
              <span className="text-xs font-medium text-gray-600">
                {severity}
              </span>
            </div>
          ))}
        </div>

        {/* Matrix rows (Likelihood 5 to 1, top to bottom) */}
        {[5, 4, 3, 2, 1].map(likelihood => (
          <div key={likelihood} className="flex">
            {/* Likelihood label */}
            <div
              className={`${labelSize} ${cellSize} flex items-center justify-center`}
            >
              <span className="text-xs font-medium text-gray-600">
                {likelihood}
              </span>
            </div>

            {/* Risk cells */}
            {[1, 2, 3, 4, 5].map(severity => {
              const score = likelihood * severity;
              const selected = isSelected(likelihood, severity);
              const isHovered = hoveredCell?.likelihood === likelihood &&
                               hoveredCell?.severity === severity;

              return (
                <button
                  key={`${likelihood}-${severity}`}
                  onClick={() => !readonly && onSelect?.(likelihood, severity)}
                  onMouseEnter={() => setHoveredCell({ likelihood, severity, score })}
                  onMouseLeave={() => setHoveredCell(null)}
                  disabled={readonly}
                  className={`
                    ${cellSize} border border-white/30 relative
                    flex items-center justify-center
                    ${getCellColor(score)}
                    ${selected ? 'ring-4 ring-blue-600 ring-inset z-10' : ''}
                    ${isHovered && !readonly ? 'ring-2 ring-white ring-inset' : ''}
                    ${!readonly ? 'cursor-pointer' : 'cursor-default'}
                    transition-all duration-150
                  `}
                  title={readonly ? undefined : `Likelihood: ${likelihood}, Severity: ${severity}, Score: ${score}`}
                >
                  <span className={`text-white font-bold ${fontSize}`}>
                    {score}
                  </span>
                  {selected && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-2 h-2 bg-white rounded-full shadow-lg" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {/* Axis labels */}
        <div className="flex mt-2">
          <div className={labelSize} />
          <div className="flex-1 text-center">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Severity →
            </span>
          </div>
        </div>

        {/* Y-axis label */}
        <div
          className="absolute -left-6 top-1/2 transform -rotate-90 -translate-y-1/2 hidden"
          style={{ display: 'none' }} // Hidden, using row labels instead
        >
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Likelihood ↑
          </span>
        </div>
      </div>

      {/* Hover tooltip */}
      {hoveredCell && !readonly && (
        <div className="p-3 bg-slate-700 text-white rounded-lg text-sm inline-block">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-gray-400">Score:</span>
            <span className="font-bold">{hoveredCell.score}</span>
            <span className="text-gray-400">Risk Level:</span>
            <span className="font-medium capitalize">{getRiskLevel(hoveredCell.score)}</span>
            <span className="text-gray-400">ALARP Zone:</span>
            <span className="font-medium">{getAlarpLabel(hoveredCell.score)}</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Risk Levels</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm text-gray-600">Low (1-5)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded" />
            <span className="text-sm text-gray-600">Medium (6-11)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-500 rounded" />
            <span className="text-sm text-gray-600">High (12-19)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm text-gray-600">Extreme (20-25)</span>
          </div>
        </div>
      </div>

      {/* ALARP Zones */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">ALARP Zones</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm text-gray-600">
              <strong>Intolerable</strong> (20-25) - Work must not proceed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded" />
            <span className="text-sm text-gray-600">
              <strong>ALARP</strong> (6-19) - Reduce As Low As Reasonably Practicable
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm text-gray-600">
              <strong>Acceptable</strong> (1-5) - Broadly acceptable risk
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact version of RiskMatrix showing just the score
 */
export const RiskScoreBadge = ({ likelihood, severity, size = 'md' }) => {
  const score = likelihood * severity;
  const level = getRiskLevel(score);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-lg'
  };

  const colorClasses = {
    extreme: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-gray-900',
    low: 'bg-green-500 text-white'
  };

  return (
    <div
      className={`
        ${sizeClasses[size]}
        ${colorClasses[level]}
        rounded-lg flex items-center justify-center font-bold
      `}
      title={`Risk Score: ${score} (${level.toUpperCase()})`}
    >
      {score}
    </div>
  );
};

/**
 * Risk level badge for display
 */
export const RiskLevelBadge = ({ score, showLabel = true }) => {
  const level = getRiskLevel(score);

  const colorClasses = {
    extreme: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200'
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
        ${colorClasses[level]}
      `}
    >
      <span className="font-bold">{score}</span>
      {showLabel && <span className="capitalize">{level}</span>}
    </span>
  );
};

export default RiskMatrix;
